export type WarningCode =
  | 'debug-enabled'
  | 'csrf-disabled'
  | 'Unauthorized payment token request'
  | 'Using legacy encryption - migrate to v2 encryption'
  | 'Using legacy decryption - migrate to v2 encryption'
  | 'Falling back to legacy decryption'

/**
 * Override any of the methods, and the rest will use the default logger.
 *
 * [Documentation](https://authjs.dev/reference/configuration/auth-config#logger)
 */
export interface LoggerInstance {
  warn: (codeOrMessage: WarningCode | string, metadata?: unknown) => void
  info: (message: string, metadata?: unknown) => void
  error: (message: string, error?: Error | unknown) => void
  debug: (message: string, metadata?: unknown) => void
}

const red = '\x1b[31m'
const yellow = '\x1b[33m'
const grey = '\x1b[90m'
const green = '\x1b[32m'
const reset = '\x1b[0m'

const isProduction = process.env.NODE_ENV === 'production'
const debugEnabled = process.env.NEXT_PUBLIC_DEBUG === 'true'

// Safe JSON serializer that handles circular references and complex objects
function safeStringify(obj: unknown, indent: number = 2): string {
  const cache = new Set()

  const replacer = (key: string, value: unknown) => {
    if (typeof value === 'object' && value !== null) {
      if (cache.has(value)) {
        return '[Circular]'
      }
      cache.add(value)

      // Handle specific problematic object types
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
        }
      }

      // Handle HTTP request/response objects by extracting key info
      if ('request' in value && 'response' in value && 'options' in value) {
        return {
          name: (value as any).name || 'HTTPError',
          message: (value as any).message || 'HTTP Request Failed',
          status: (value as any).response?.status,
          statusText: (value as any).response?.statusText,
          url: (value as any).request?.url || (value as any).options?.url,
          method: (value as any).options?.method,
        }
      }

      // Handle DOM elements, functions, etc.
      if (typeof value === 'function') {
        return '[Function]'
      }

      if (value instanceof Element) {
        return '[DOM Element]'
      }
    }

    return value
  }

  try {
    return JSON.stringify(obj, replacer, indent)
  } catch (error) {
    return '[Unable to serialize object]'
  }
}

export const logger: LoggerInstance = {
  error(message: string, error: Error | unknown = null) {
    if (error) {
      console.error(safeStringify(error, 2))
    }
    const errorObj = error as Error
    const url = `https://errors.authjs.dev#${errorObj?.name?.toLowerCase()}`
    console.error(
      `[${red}ERROR: ${errorObj?.name ?? ''}${reset}]: ${message}${
        errorObj?.message ? `-  ${errorObj?.message}.` : ''
      }${url ? ` Read more at ${url}` : ''}`,
    )
    if (error && typeof error === 'object' && 'cause' in error) {
      const cause = error.cause as { err?: Error; [key: string]: unknown }
      const { err, ...data } = cause
      console.error(`[${red}CAUSE${reset}]:`, err?.stack)
      console.error(`[${grey}DETAILS${reset}]:`, safeStringify(data, 2))
    } else if (error && typeof error === 'object' && 'stack' in error) {
      const stack = (error as Error).stack
      if (stack) {
        console.error(stack.replace(/.*/, '').substring(1))
      }
    }
  },
  warn(codeOrMessage, metadata) {
    if (typeof codeOrMessage === 'string' && !['debug-enabled', 'csrf-disabled'].includes(codeOrMessage)) {
      // It's a message, not a code
      if (metadata) {
        console.warn(`${yellow}[warn]${reset}`, codeOrMessage, metadata)
      } else {
        console.warn(`${yellow}[warn]${reset}`, codeOrMessage)
      }
    } else {
      // It's a warning code
      const url = `https://warnings.authjs.dev#${codeOrMessage}`
      console.warn(`${yellow}[warn][${codeOrMessage}]${reset}`, `Read more: ${url}`)
    }
  },
  debug(message, metadata) {
    if (!isProduction || debugEnabled) {
      if (metadata) console.log(`[${grey}DEBUG${reset}]: ${message}`, safeStringify(metadata, 2))
      else console.log(`[${grey}DEBUG${reset}]: ${message}`)
    }
  },
  info(message, metadata) {
    if (!isProduction || debugEnabled) {
      if (metadata) console.log(`[${green}INFO${reset}]: ${message}`, safeStringify(metadata, 2))
      else console.log(`[${green}INFO${reset}]: ${message}`)
    }
  },
}

/**
 * Override the built-in logger with user's implementation.
 * Any `undefined` level will use the default logger.
 */
export function setLogger(newLogger: Partial<LoggerInstance> = {}, debug?: boolean) {
  // Turn off debug logging if `debug` isn't set to `true`
  if (!debug) logger.debug = () => {}

  if (newLogger.error) logger.error = newLogger.error
  if (newLogger.warn) logger.warn = newLogger.warn
  if (newLogger.debug) logger.debug = newLogger.debug
}
